import type { ReactNode } from "react";
import { useFilter } from "react-aria";
import {
  Autocomplete,
  Input as RAInput,
  SearchField,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { m } from "../paraglide/messages";
import Spinner from "../Spinner";
import type { SelectItem, SlotClassNames } from "../types";
import { SelectListbox } from "./SelectListbox";

type Key = string | number;

const searchableListboxStyles = tv({
  slots: {
    base: "bg-secondary overflow-hidden rounded-md",
    listbox: "",
  },
});

export function NoResults() {
  return (
    <div className="text-text-secondary px-3 py-4 text-center text-sm">
      {m.no_results_found()}
    </div>
  );
}

/** search field that filters the listbox passed as children by label and description */
export function SearchableListShell<TItem extends SelectItem<Key>>({
  accessibleLabel,
  searchPlaceholder,
  isLoading,
  autoFocus,
  inputValue,
  onInputChange,
  children,
}: {
  readonly accessibleLabel?: string;
  readonly searchPlaceholder?: string;
  readonly isLoading: boolean;
  readonly autoFocus?: boolean;
  readonly inputValue?: string;
  readonly onInputChange?: (value: string) => void;
  readonly children: ReactNode;
}) {
  const { contains } = useFilter({ sensitivity: "base" });

  return (
    <Autocomplete<TItem>
      inputValue={inputValue}
      onInputChange={onInputChange}
      filter={(textValue, inputValue, node) =>
        contains(textValue, inputValue) ||
        (!!node.value?.description &&
          contains(node.value.description, inputValue))
      }
    >
      <SearchField
        aria-label={accessibleLabel}
        autoFocus={autoFocus}
        className="border-border block shrink-0 border-b p-2"
      >
        <RAInput
          placeholder={searchPlaceholder ?? "Search..."}
          autoComplete="off"
          className={`bg-tertiary text-text-primary placeholder:text-text-tertiary h-8 w-full rounded-md px-3 text-sm focus:outline-0`}
        />
      </SearchField>

      {isLoading ? (
        <div className="flex items-center justify-center px-3 py-4">
          <div className="text-text-secondary text-sm">
            <Spinner size="xs" /> {m.loading_options()}
          </div>
        </div>
      ) : (
        children
      )}
    </Autocomplete>
  );
}

export type SearchableListboxProps<K extends Key, T extends SelectItem<K>> = {
  readonly items: readonly T[];
  readonly value: readonly K[];
  readonly onChange: (value: K[]) => void;
  readonly "aria-label": string;
  readonly searchPlaceholder?: string;
  readonly isLoading?: boolean;
  readonly autoFocus?: boolean;
  readonly className?: string;
  readonly classNames?: SlotClassNames<
    ReturnType<typeof searchableListboxStyles>
  >;
};

/** always-visible multi-select checklist with a search field, the inline counterpart of a searchable Select */
export function SearchableListbox<K extends Key, T extends SelectItem<K>>({
  items,
  value,
  onChange,
  "aria-label": ariaLabel,
  searchPlaceholder,
  isLoading = false,
  autoFocus,
  className,
  classNames,
}: SearchableListboxProps<K, T>) {
  const styles = searchableListboxStyles();
  return (
    <div
      className={styles.base({
        className: twMerge(className, classNames?.base),
      })}
    >
      <SearchableListShell<T>
        accessibleLabel={ariaLabel}
        searchPlaceholder={searchPlaceholder}
        isLoading={isLoading}
        autoFocus={autoFocus}
      >
        <SelectListbox<K, T>
          items={items}
          aria-label={ariaLabel}
          selectionMode="multiple"
          selectedKeys={value}
          onSelectionChange={(keys) =>
            onChange(
              keys === "all"
                ? items
                    .filter((item) => !item.disabled)
                    .map((item) => item.value)
                : [...keys],
            )
          }
          className={styles.listbox({ className: classNames?.listbox })}
          renderEmptyState={() => <NoResults />}
        />
      </SearchableListShell>
    </div>
  );
}
